import { t } from 'i18next';
import * as yup from 'yup';

import {
  STT_PROVIDER_LANGUAGE_MAP,
  TTS_PROVIDER_LANGUAGE_MAP,
  resolveDeepgramLanguage,
} from '@/constants/voiceProviderLanguageMap';
import { WAITING_SOUND_NAMES } from '@/hooks/useWaitingSounds';
import { BrainLanguage } from '@/shared/types/brain';
import {
  AMD_MODES,
  Integration,
  IntegrationType,
  ZendeskConfig,
  IntercomConfig,
  WebWidgetConfig,
  SuncoConfig,
  FacebookConfig,
  InstagramConfig,
  ViberConfig,
  MstatViberConfig,
  WhatsAppConfig,
  FrontConfig,
  InfobipSmsConfig,
  InfobipRcsConfig,
  SmartleadConfig,
  BaseVoiceConfig,
  TwilioVoiceConfig,
  TelnyxVoiceConfig,
  WebVoiceConfig,
} from '@/shared/types/integration';
import { DEEPGRAM_EN_ONLY_MODEL, type STTProvider } from '@/shared/types/voiceProviders';
import { DEFAULT_START_MESSAGE } from '@/util/constants';

/**
 * Builds the shared voice pipeline defaults (STT, TTS, language normalisation)
 * for any provider extending BaseVoiceConfig. The caller spreads
 * provider-specific fields (credentials, allowed_origins, etc.) on top.
 */
const buildVoiceConfigDefaults = (
  baseConfig: Record<string, unknown>,
  voiceConfig: BaseVoiceConfig | undefined,
  defaults: BaseVoiceConfig | undefined,
  defaultSttProvider: string
) => {
  // Integrations saved with the removed OpenAI Realtime STT fall back to the default provider.
  const storedSttProvider = voiceConfig?.stt_provider;
  const sttProvider =
    storedSttProvider && storedSttProvider in STT_PROVIDER_LANGUAGE_MAP
      ? storedSttProvider
      : defaultSttProvider;
  const ttsProvider = voiceConfig?.tts_provider || 'elevenlabs';
  const { stt_openai_realtime: _removedSttConfig, ...storedConfig } = (voiceConfig ??
    {}) as BaseVoiceConfig & { stt_openai_realtime?: unknown };

  const configResult = {
    ...baseConfig,
    ...storedConfig,
    // Legacy / unmatched configs default to 'custom' so existing settings are preserved.
    preset: voiceConfig?.preset ?? 'custom',
    welcome_trigger_message: voiceConfig?.welcome_trigger_message || '',
    stt_provider: sttProvider,
    tts_provider: ttsProvider,
    // --- STT providers ---
    stt_deepgram: {
      model:
        voiceConfig?.stt_deepgram?.model || defaults?.stt_deepgram?.model || DEEPGRAM_EN_ONLY_MODEL,
      language: voiceConfig?.stt_deepgram?.language || defaults?.stt_deepgram?.language || 'en',
      language_hint:
        voiceConfig?.stt_deepgram?.language_hint || defaults?.stt_deepgram?.language_hint,
      ...voiceConfig?.stt_deepgram,
    },
    stt_elevenlabs: {
      language_code:
        voiceConfig?.stt_elevenlabs?.language_code ||
        defaults?.stt_elevenlabs?.language_code ||
        'en',
      ...voiceConfig?.stt_elevenlabs,
    },
    stt_soniox: {
      language_hints: voiceConfig?.stt_soniox?.language_hints || [
        voiceConfig?.tts_elevenlabs?.language_code || 'en',
      ],
      language_hints_strict: voiceConfig?.stt_soniox?.language_hints_strict ?? true,
      ...voiceConfig?.stt_soniox,
    },
    // --- TTS providers ---
    tts_elevenlabs: (() => {
      const { pronunciation_dictionary_locators, api_key, ...elevenlabsRest } =
        voiceConfig?.tts_elevenlabs ?? {};
      return {
        api_key: api_key ?? undefined,
        language_code:
          elevenlabsRest.language_code || defaults?.tts_elevenlabs?.language_code || 'en',
        model_id:
          elevenlabsRest.model_id || defaults?.tts_elevenlabs?.model_id || 'eleven_turbo_v2_5',
        voice_settings: {
          stability:
            elevenlabsRest.voice_settings?.stability ??
            defaults?.tts_elevenlabs?.voice_settings?.stability ??
            0.5,
          speed:
            elevenlabsRest.voice_settings?.speed ??
            defaults?.tts_elevenlabs?.voice_settings?.speed ??
            1.0,
          similarity_boost:
            elevenlabsRest.voice_settings?.similarity_boost ??
            defaults?.tts_elevenlabs?.voice_settings?.similarity_boost ??
            0.75,
          ...elevenlabsRest.voice_settings,
        },
        apply_text_normalization:
          elevenlabsRest.apply_text_normalization ||
          defaults?.tts_elevenlabs?.apply_text_normalization ||
          'auto',
        ...elevenlabsRest,
        // ElevenLabs API stores dicts as an array of locators; the form exposes a single-id field
        // (toggle component writes/clears the id). Repacked into locators on save.
        pronunciation_dictionary_id:
          pronunciation_dictionary_locators?.[0]?.pronunciation_dictionary_id,
        pronunciation_dictionary_version_id: pronunciation_dictionary_locators?.[0]?.version_id,
      };
    })(),
    tts_cartesia: {
      language_code:
        voiceConfig?.tts_cartesia?.language_code || defaults?.tts_cartesia?.language_code || 'en',
      model_id: voiceConfig?.tts_cartesia?.model_id,
      voice_id: voiceConfig?.tts_cartesia?.voice_id,
      volume: voiceConfig?.tts_cartesia?.volume ?? defaults?.tts_cartesia?.volume ?? 1.0,
      speed: voiceConfig?.tts_cartesia?.speed ?? defaults?.tts_cartesia?.speed ?? 1.0,
      emotion: voiceConfig?.tts_cartesia?.emotion || defaults?.tts_cartesia?.emotion || 'neutral',
      pronunciation_dictionary_id: voiceConfig?.tts_cartesia?.pronunciation_dictionary_id,
    },
    tts_minimax: {
      language_boost:
        voiceConfig?.tts_minimax?.language_boost || defaults?.tts_minimax?.language_boost || 'auto',
      model_id: voiceConfig?.tts_minimax?.model_id,
      voice_id: voiceConfig?.tts_minimax?.voice_id,
      speed: voiceConfig?.tts_minimax?.speed ?? defaults?.tts_minimax?.speed ?? 1.0,
      volume: voiceConfig?.tts_minimax?.volume ?? defaults?.tts_minimax?.volume ?? 1.0,
      pitch: voiceConfig?.tts_minimax?.pitch ?? defaults?.tts_minimax?.pitch ?? 0,
      english_normalization:
        voiceConfig?.tts_minimax?.english_normalization ??
        defaults?.tts_minimax?.english_normalization ??
        false,
    },
  };

  // --- Language normalisation ---
  const brainLanguageValues = Object.values(BrainLanguage) as string[];

  const canonicalLang: string = (() => {
    if (voiceConfig?.language && brainLanguageValues.includes(voiceConfig.language)) {
      return voiceConfig.language;
    }
    const sttMapping = STT_PROVIDER_LANGUAGE_MAP[sttProvider as STTProvider];
    const rawLang = (() => {
      switch (sttProvider) {
        case 'deepgram':
          return configResult.stt_deepgram?.language;
        case 'elevenlabs':
          return configResult.stt_elevenlabs?.language_code;
        case 'soniox':
          return configResult.stt_soniox?.language_hints;
        default:
          return undefined;
      }
    })();
    const storedLang = sttMapping.fromProvider(rawLang) || 'en';
    return brainLanguageValues.includes(storedLang)
      ? storedLang
      : (brainLanguageValues.find((bl) => bl.startsWith(storedLang)) ?? 'en');
  })();

  configResult.language = canonicalLang;
  configResult.stt_deepgram.language = resolveDeepgramLanguage(
    canonicalLang,
    configResult.stt_deepgram.model
  );
  configResult.stt_elevenlabs.language_code = STT_PROVIDER_LANGUAGE_MAP.elevenlabs.toProvider(
    canonicalLang
  ) as string;
  configResult.stt_soniox.language_hints = STT_PROVIDER_LANGUAGE_MAP.soniox.toProvider(
    canonicalLang
  ) as string[];
  configResult.tts_elevenlabs.language_code = TTS_PROVIDER_LANGUAGE_MAP.elevenlabs.toProvider(
    canonicalLang
  ) as string;
  configResult.tts_cartesia.language_code = TTS_PROVIDER_LANGUAGE_MAP.cartesia.toProvider(
    canonicalLang
  ) as string;
  configResult.tts_minimax.language_boost = TTS_PROVIDER_LANGUAGE_MAP.minimax.toProvider(
    canonicalLang
  ) as string;

  return configResult;
};

/**
 * Default configuration values for each integration type.
 * These values are used as fallbacks when creating new integrations
 * or when existing configurations don't have certain fields set.
 */
const VOICE_CONFIG_DEFAULTS = {
  language: 'en',
  welcome_trigger_message: DEFAULT_START_MESSAGE,
  // Default STT provider is Soniox
  stt_provider: 'soniox',
  stt_soniox: {
    model: 'stt-rt-v5',
    language_hints: ['en'],
    language_hints_strict: true,
    endpoint_sensitivity: -0.5,
    max_endpoint_delay_ms: 2500,
  },
  // Default TTS provider is ElevenLabs for backward compatibility
  tts_provider: 'elevenlabs',
  tts_elevenlabs: {
    language_code: 'en',
    model_id: 'eleven_turbo_v2_5',
    voice_settings: { stability: 0.75, speed: 1.0, similarity_boost: 0.75 },
    apply_text_normalization: 'auto',
  },
  tts_cartesia: { language_code: 'en', volume: 1.0, speed: 1.0, emotion: 'neutral' },
  tts_minimax: {
    language_boost: 'auto',
    speed: 1.0,
    volume: 1.0,
    pitch: 0,
    english_normalization: false,
  },
};

const WEB_VOICE_CONFIG_DEFAULTS = {
  ...VOICE_CONFIG_DEFAULTS,
  allowed_origins: [],
  ice_transport_policy: 'all',
};

export const DEFAULT_INTEGRATION_CONFIG: Partial<
  Record<IntegrationType, { config: Record<string, unknown> }>
> = {
  [IntegrationType.WEB]: {
    config: { show_powered_by: true, launcher: { type: 'bubble-sparkle' } },
  },
  [IntegrationType.TWILIO_VOICE]: { config: { ...VOICE_CONFIG_DEFAULTS } },
  [IntegrationType.TELNYX_VOICE]: { config: { ...VOICE_CONFIG_DEFAULTS } },
  [IntegrationType.WEB_VOICE]: { config: WEB_VOICE_CONFIG_DEFAULTS },
};

/**
 * Maps integration type to its corresponding config type and extracts default values
 * for form prefilling based on the integration's current configuration
 */
export function getIntegrationFormDefaults(integration: Integration | undefined) {
  if (!integration) {
    return {};
  }

  const { type, config, active } = integration;

  // Base defaults that all integrations share - include type for yup.lazy() validation
  const baseDefaults = { type, active: active || false, config: {} };

  //TODO: Populate the default values progressively when migrating integrations to new UI.

  // Type-specific config mapping
  switch (type) {
    case 'zendesk':
      return { ...baseDefaults, config: { ...baseDefaults.config, ...(config as ZendeskConfig) } };

    case 'intercom':
      return { ...baseDefaults, config: { ...baseDefaults.config, ...(config as IntercomConfig) } };

    case 'web':
      return {
        ...baseDefaults,
        config: { ...baseDefaults.config, ...(config as WebWidgetConfig) },
      };

    case 'sunco':
      return { ...baseDefaults, config: { ...baseDefaults.config, ...(config as SuncoConfig) } };

    case 'facebook':
      return { ...baseDefaults, config: { ...baseDefaults.config, ...(config as FacebookConfig) } };

    case 'instagram':
      return {
        ...baseDefaults,
        config: { ...baseDefaults.config, ...(config as InstagramConfig) },
      };

    case 'viber':
      return { ...baseDefaults, config: { ...baseDefaults.config, ...(config as ViberConfig) } };

    case 'mstat-viber':
      return {
        ...baseDefaults,
        config: { ...baseDefaults.config, ...(config as MstatViberConfig) },
      };

    case 'whatsapp':
      return { ...baseDefaults, config: { ...baseDefaults.config, ...(config as WhatsAppConfig) } };

    case 'front':
      return { ...baseDefaults, config: { ...baseDefaults.config, ...(config as FrontConfig) } };

    case 'infobip-sms':
      return {
        ...baseDefaults,
        config: { ...baseDefaults.config, ...(config as InfobipSmsConfig) },
      };

    case 'infobip-rcs':
      return {
        ...baseDefaults,
        config: { ...baseDefaults.config, ...(config as InfobipRcsConfig) },
      };

    case 'smartlead':
      return {
        ...baseDefaults,
        config: { ...baseDefaults.config, ...(config as SmartleadConfig) },
      };

    case 'twilio-voice': {
      const defaults = DEFAULT_INTEGRATION_CONFIG[IntegrationType.TWILIO_VOICE]
        ?.config as unknown as BaseVoiceConfig | undefined;
      const twilioConfig = config as TwilioVoiceConfig;
      const voiceDefaults = buildVoiceConfigDefaults(
        baseDefaults.config,
        twilioConfig,
        defaults,
        'deepgram'
      );
      return {
        ...baseDefaults,
        config: {
          ...voiceDefaults,
          phone_number: twilioConfig?.phone_number ?? '',
          account_sid: twilioConfig?.account_sid || '',
          auth_token: twilioConfig?.auth_token || '',
        },
      };
    }

    case 'telnyx-voice': {
      const defaults = DEFAULT_INTEGRATION_CONFIG[IntegrationType.TELNYX_VOICE]
        ?.config as unknown as BaseVoiceConfig | undefined;
      const telnyxConfig = config as TelnyxVoiceConfig;
      // TelnyxVoiceConfig.denoiser is incompatible with BaseVoiceConfig.denoiser
      // (Telnyx widens `provider` to 'moveo' | 'telnyx'). buildVoiceConfigDefaults
      // spreads voiceConfig into the result without narrowing the denoiser shape,
      // so the cast preserves the wider Telnyx value through to the form unchanged.
      const voiceDefaults = buildVoiceConfigDefaults(
        baseDefaults.config,
        telnyxConfig as unknown as BaseVoiceConfig,
        defaults,
        'soniox'
      );
      return {
        ...baseDefaults,
        config: {
          ...voiceDefaults,
          phone_number: telnyxConfig?.phone_number ?? '',
          api_key: telnyxConfig?.api_key || '',
          public_key: telnyxConfig?.public_key || '',
        },
      };
    }

    case 'web-voice': {
      const defaults = DEFAULT_INTEGRATION_CONFIG[IntegrationType.WEB_VOICE]?.config as unknown as
        BaseVoiceConfig | undefined;
      const webVoiceConfig = config as WebVoiceConfig;
      const voiceDefaults = buildVoiceConfigDefaults(
        baseDefaults.config,
        webVoiceConfig,
        defaults,
        'soniox'
      );
      return {
        ...baseDefaults,
        config: {
          ...voiceDefaults,
          allowed_origins: webVoiceConfig?.allowed_origins ?? [],
          ice_transport_policy: webVoiceConfig?.ice_transport_policy ?? 'all',
        },
      };
    }

    default:
      return baseDefaults;
  }
}

// Phone number validation schema (optional field)
const phoneSchema = yup
  .string()
  .trim()
  .max(24)
  .test('phone-format', 'Invalid phone number format', (value) => {
    // Allow empty/undefined values since phone_number is optional
    if (!value) {
      return true;
    }
    // Validate format if value is provided
    return /^\+[1-9]\d{1,14}$/.test(value);
  });

// Visitor language validation — AI Agent supported language codes
const providerLanguageSchema = yup.string().oneOf(Object.values(BrainLanguage));

/**
 * Shared voice pipeline schema — STT/TTS/language/recording validation
 * common to both Twilio Voice and Telnyx Voice integrations.
 */
const getVoiceConfigSchema = () =>
  yup.object({
    // Configuration preset selector
    preset: yup.string().oneOf(['en', 'el', 'pt-br', 'custom']),

    // Voice configuration
    welcome_trigger_message: yup.string().max(64),

    call_recording: yup.boolean().default(false),

    // Canonical language — single source of truth for the UI
    language: providerLanguageSchema,

    // Phone number configuration
    phone_number: phoneSchema,

    stt_provider: yup.string().oneOf(['deepgram', 'elevenlabs', 'soniox']),

    // Speech-to-Text Configuration (Deepgram) - conditionally validated based on stt_provider
    stt_deepgram: yup
      .object({
        model: yup.string(),
        // Language validation is handled by the /provider API which returns model-specific supported languages
        language: yup.string(),
        // Flux model settings
        eot_threshold: yup
          .number()
          .label(t('integrations.voice_common.transcription.eot_threshold'))
          .min(0.5)
          .max(0.9),
        eot_timeout_ms: yup
          .number()
          .label(t('integrations.voice_common.transcription.eot_timeout_ms'))
          .integer()
          .min(500)
          .max(10000),
        // Nova model settings
        endpointing: yup
          .number()
          .label(t('integrations.voice_common.transcription.endpointing'))
          .integer()
          .min(0)
          .max(5000),
        utterance_end_ms: yup
          .number()
          .label(t('integrations.voice_common.transcription.utterance_end_ms'))
          .integer()
          .min(1000)
          .max(5000),
        // Keywords for Nova 2 (format: "word:intensifier")
        keywords: yup.array().of(yup.string().max(128)),
        // Key terms for Flux and Nova 3
        keyterm: yup.array().of(yup.string().max(128)),
      })
      .optional()
      .default(undefined)
      .when('stt_provider', {
        is: 'deepgram',
        then: (schema) =>
          schema.shape({
            // Model is required when using Deepgram as STT provider
            model: yup.string().required('Model is required when using Deepgram'),
          }),
      }),

    // Speech-to-Text Configuration (ElevenLabs Scribe v2 Realtime)
    stt_elevenlabs: yup
      .object({
        model_id: yup.string().max(128),
        // Provider-specific code (e.g. 'fil' for Tagalog) — backend validates against ELEVENLABS_STT_LANGUAGES
        language_code: yup.string(),
        commit_strategy: yup.string().oneOf(['manual', 'vad']),
        vad_silence_threshold_secs: yup
          .number()
          .label(t('integrations.voice_common.transcription.vad_silence_threshold_secs'))
          .nonNullable()
          .min(0.3)
          .max(3),
        vad_threshold: yup
          .number()
          .label(t('integrations.voice_common.transcription.vad_threshold'))
          .nonNullable()
          .min(0.1)
          .max(0.9),
        min_speech_duration_ms: yup
          .number()
          .label(t('integrations.voice_common.transcription.min_speech_duration_ms'))
          .integer()
          .min(50)
          .max(2000),
        min_silence_duration_ms: yup
          .number()
          .label(t('integrations.voice_common.transcription.min_silence_duration_ms'))
          .integer()
          .min(50)
          .max(2000),
        include_timestamps: yup.boolean(),
        include_language_detection: yup.boolean(),
        enable_logging: yup.boolean(),
      })
      .optional()
      .default(undefined)
      .when('stt_provider', {
        is: 'elevenlabs',
        then: (schema) =>
          schema.shape({
            model_id: yup.string().required('Model is required when using ElevenLabs'),
          }),
      }),

    // Speech-to-Text Configuration (Soniox)
    stt_soniox: yup
      .object({
        model: yup.string().max(128),
        language_hints: yup.array().of(yup.string().max(16)).max(10),
        language_hints_strict: yup.boolean(),
        max_endpoint_delay_ms: yup
          .number()
          .label(t('integrations.voice_common.transcription.max_endpoint_delay_ms'))
          .integer()
          .min(500)
          .max(3000),
        endpoint_sensitivity: yup
          .number()
          .label(t('integrations.voice_common.transcription.endpoint_sensitivity'))
          .min(-1)
          .max(1),
        context: yup.object({
          text: yup.string().max(10000),
          terms: yup.array().of(yup.string().max(128)).max(100),
        }),
        enable_speaker_diarization: yup.boolean(),
        speaker_policy: yup.string().oneOf(['primary_only', 'all_speakers']),
      })
      .optional()
      .default(undefined)
      .when('stt_provider', {
        is: 'soniox',
        then: (schema) =>
          schema.shape({ model: yup.string().required('Model is required when using Soniox') }),
      }),

    // TTS Provider Selection
    tts_provider: yup.string().oneOf(['elevenlabs', 'cartesia', 'minimax']),

    // Text-to-Speech Configuration (ElevenLabs)
    tts_elevenlabs: yup.object({
      api_key: yup
        .string()
        .nullable()
        .max(256)
        .test(
          'api-key-required-when-set',
          t('integrations.voice_common.voice.api_key_required'),
          (value) => value !== ''
        ),
      region: yup.string().oneOf(['global', 'eu', 'in']).nullable(),
      voice_id: yup.string().max(128),
      model_id: yup.string().max(128),
      // Provider-specific code (e.g. 'fil' for Tagalog) — backend validates against ElevenLabs model lists
      language_code: yup.string(),
      apply_text_normalization: yup.string().oneOf(['auto', 'on', 'off']),
      voice_settings: yup.object({
        stability: yup.number().min(0.0).max(1.0),
        similarity_boost: yup.number().min(0.0).max(1.0),
        speed: yup.number().min(0.7).max(1.2),
      }),
      pronunciation_dictionary_id: yup.string().max(128),
      pronunciation_dictionary_version_id: yup.string().max(128),
    }),

    // Text-to-Speech Configuration (Cartesia)
    tts_cartesia: yup.object({
      voice_id: yup.string().max(128),
      model_id: yup.string().max(128),
      // Provider-specific code — backend validates against Cartesia's language list
      language_code: yup.string(),
      volume: yup.number().min(0).max(2),
      speed: yup.number().min(0).max(2),
      emotion: yup.string().oneOf(['neutral', 'calm', 'angry', 'content', 'sad', 'scared']),
      pronunciation_dictionary_id: yup.string().max(128),
    }),

    // Text-to-Speech Configuration (MiniMax)
    tts_minimax: yup.object({
      voice_id: yup.string().max(128),
      model_id: yup.string().oneOf(['speech-2.8-hd', 'speech-2.8-turbo']),
      // ISO code (or 'auto'); backend maps to the MiniMax language name
      language_boost: yup.string(),
      speed: yup.number().min(0.5).max(2),
      // Orchestrator enforces volume > 0 (Joi .greater(0)); 0 is rejected.
      volume: yup.number().moreThan(0).max(10),
      pitch: yup.number().min(-12).max(12),
      english_normalization: yup.boolean(),
    }),

    // Thinking sound configuration (waiting/processing indicator)
    thinking_sound: yup
      .object({
        // Name of the sound asset in S3 /media/sounds
        // Empty string means no sound selected
        name: yup
          .string()
          .oneOf([...WAITING_SOUND_NAMES, ''])
          .optional(),
      })
      .optional(),

    // Denoiser configuration - base layer accepts only the Moveo provider.
    // Telnyx-voice integrations widen this, to allow server-side suppression with an engine + direction.
    denoiser: yup
      .object({
        enabled: yup.boolean(),
        provider: yup.string().oneOf(['moveo']).optional(),
        moveo: yup
          .object()
          .when('provider', {
            is: 'moveo',
            then: (s) =>
              s
                .shape({
                  engine: yup
                    .string()
                    .label(t('integrations.voice_common.engine_label'))
                    .oneOf(['gtcrn', 'quail-voice-focus'])
                    .required(),
                })
                .required(),
            otherwise: (s) => s.optional(),
          }),
      })
      .optional()
      .default(undefined),
  });

// Channels reads "From" (any case) or an X- SIP header name; any other value fills no phone.
const VOICE_PSID_FIELD_PATTERN = /^(from|x-[a-z0-9.!%*_+`'~-]+)$/i;

const getVoicePsidFieldSchema = () =>
  yup
    .string()
    .max(64)
    .matches(VOICE_PSID_FIELD_PATTERN, {
      message: t('integrations.voice_common.sip_caller.invalid'),
      excludeEmptyString: true,
    });

/**
 * Twilio Voice integration config schema — Twilio credentials + shared voice pipeline
 */
export const getTwilioConfigSchema = () =>
  yup.object({
    account_sid: yup
      .string()
      .label(t('integrations.twilio_voice_connect.account_sid.title'))
      .max(128)
      .required(),
    auth_token: yup
      .string()
      .label(t('integrations.twilio_voice_connect.auth_token.title'))
      .max(128)
      .required(),
    transcript_relay_enabled: yup.boolean().default(false),
    voice_psid_field: getVoicePsidFieldSchema(),
    // Answering machine detection. No `mode`: channels gates the Twilio dial on `enabled`
    // alone. `enabled` is required because the orchestrator rejects `amd: {}`.
    amd: yup.object({ enabled: yup.boolean().required() }).optional().default(undefined),
    ...getVoiceConfigSchema().fields,
  });

/**
 * BYOC egress SIP digest auth is both-or-neither. Enforced at save time (not
 * per-keystroke — the form validates onChange). True when both set or both empty.
 */
export const isEgressSipAuthPairValid = (username?: string, password?: string): boolean =>
  !!username === !!password;

/**
 * Telnyx Voice integration config schema — Telnyx credentials + shared voice pipeline
 */
export const getTelnyxConfigSchema = () =>
  yup.object({
    api_key: yup
      .string()
      .label(t('integrations.telnyx_voice_connect.api_key.title'))
      .max(256)
      .required(),
    public_key: yup
      .string()
      .label(t('integrations.telnyx_voice_connect.public_key.title'))
      .max(256)
      .required(),
    ...getVoiceConfigSchema().fields,
    // Override the base denoiser shape to allow Telnyx server-side suppression.
    denoiser: yup
      .object({
        enabled: yup.boolean(),
        provider: yup.string().oneOf(['moveo', 'telnyx']).optional(),
        moveo: yup
          .object()
          .when('provider', {
            is: 'moveo',
            then: (s) =>
              s
                .shape({
                  engine: yup
                    .string()
                    .label(t('integrations.voice_common.engine_label'))
                    .oneOf(['gtcrn', 'quail-voice-focus'])
                    .required(),
                })
                .required(),
            otherwise: (s) => s.optional(),
          }),
        telnyx: yup
          .object()
          .when('provider', {
            is: 'telnyx',
            then: (s) =>
              s
                .shape({
                  engine: yup
                    .string()
                    .oneOf([
                      'Denoiser',
                      'DeepFilterNet',
                      'Krisp Viva Tel',
                      'Krisp Viva Tel Lite',
                      'Krisp Viva Pro',
                      'Krisp Viva SS',
                      'AI-coustics Quail',
                    ])
                    .required(),
                  direction: yup.string().oneOf(['inbound', 'outbound', 'both']).required(),
                })
                .required(),
            otherwise: (s) => s.optional(),
          }),
      })
      .optional()
      .default(undefined),
    // Answering machine detection. AMD_MODES is shared with the TS type and the card UI;
    // the orchestrator is the gatekeeper and rejects unknown modes.
    amd: yup
      .object({
        enabled: yup.boolean().required(),
        mode: yup
          .string()
          .label(t('integrations.voice_common.amd_mode_label'))
          .oneOf([...AMD_MODES])
          .optional(),
      })
      .optional()
      .default(undefined),
    // BYOC egress SIP digest auth. Both-or-neither is enforced at save time
    // (isEgressSipAuthPairValid), not per-keystroke; blank clears (disables).
    egress_sip_auth_username: yup.string().max(128),
    egress_sip_auth_password: yup.string().max(128),
    voice_psid_field: getVoicePsidFieldSchema(),
  });

export const getWebVoiceConfigSchema = () =>
  yup.object({
    allowed_origins: yup
      .array()
      .of(
        yup
          .string()
          .label(t('integrations.web_voice_connect.allowed_origins.title'))
          .max(256)
          .test('origin', t('integrations.web_voice_connect.allowed_origins.invalid'), (value) => {
            if (!value) {
              return true;
            }
            try {
              return new URL(value).origin === value;
            } catch {
              return false;
            }
          })
          .required()
      )
      .max(20)
      .default([]),
    ice_transport_policy: yup.string().oneOf(['all', 'relay']).default('all'),
    ...getVoiceConfigSchema().fields,
  });

/** The voice integration's rules for one provider block, e.g. `stt_soniox`, reused by the agent speech strategy. */
export const getProviderBlockSchema = (block: string): yup.ISchema<unknown> | undefined => {
  const { fields } = getVoiceConfigSchema();
  return block in fields
    ? (fields[block as keyof typeof fields] as yup.ISchema<unknown>)
    : undefined;
};

export const normalizeCartesiaPronunciationDictionary = (
  ttsCartesia: Record<string, unknown> | undefined
): void => {
  if (!ttsCartesia) {
    return;
  }
  if (!ttsCartesia.pronunciation_dictionary_id) {
    delete ttsCartesia.pronunciation_dictionary_id;
  }
};

export const repackElevenLabsPronunciationDictionary = (
  ttsElevenLabs: Record<string, unknown> | undefined
): void => {
  if (!ttsElevenLabs) {
    return;
  }
  const id = ttsElevenLabs.pronunciation_dictionary_id;
  const versionId = ttsElevenLabs.pronunciation_dictionary_version_id;
  delete ttsElevenLabs.pronunciation_dictionary_id;
  delete ttsElevenLabs.pronunciation_dictionary_version_id;
  if (typeof id === 'string' && id) {
    const locator: { pronunciation_dictionary_id: string; version_id?: string } = {
      pronunciation_dictionary_id: id,
    };
    if (typeof versionId === 'string' && versionId) {
      locator.version_id = versionId;
    }
    ttsElevenLabs.pronunciation_dictionary_locators = [locator];
  } else {
    delete ttsElevenLabs.pronunciation_dictionary_locators;
  }
};

export const normalizeSttKeywords = (config: Record<string, unknown> | undefined): void => {
  if (!config) {
    return;
  }
  const dropEmpty = (terms: string[]) => terms.filter((term) => term?.trim());

  const deepgram = config.stt_deepgram as Record<string, unknown> | undefined;
  if (deepgram) {
    if (Array.isArray(deepgram.keyterm)) {
      deepgram.keyterm = dropEmpty(deepgram.keyterm as string[]);
    }
    if (Array.isArray(deepgram.keywords)) {
      // Stored as "word:intensifier" — drop rows whose word part is empty.
      deepgram.keywords = (deepgram.keywords as string[]).filter((kw) => kw?.split(':')[0]?.trim());
    }
  }

  const sonioxContext = (config.stt_soniox as Record<string, unknown> | undefined)?.context as
    Record<string, unknown> | undefined;
  if (sonioxContext && Array.isArray(sonioxContext.terms)) {
    sonioxContext.terms = dropEmpty(sonioxContext.terms as string[]);
  }
};

export const integrationsSchema = yup
  .object({
    type: yup.string(),
    name: yup.string(),
    active: yup.boolean(),
    config: yup.lazy((_value, { parent }) => {
      const type = parent?.type;

      switch (type) {
        case 'twilio-voice':
          return getTwilioConfigSchema();
        case 'telnyx-voice':
          return getTelnyxConfigSchema();
        case 'web-voice':
          return getWebVoiceConfigSchema();
        default:
          return yup.object({}).required('Config is required for unknown integration type');
      }
    }),
  })
  .noUnknown(true)
  .strict();
